import React from 'react';
import styled from 'styled-components';
import MediaQuery from 'react-responsive';
import { AlertTooltip } from '../Spreadsheet/ModTooltip';
import { AttributeItem } from './types';
import { formatPrice } from '../Spreadsheet/Spreadsheet';
import { formatDateUA, formatDateTimeUA } from '../../helpers/formateDate';


const Row = styled.div`
  display: flex;
  flex-direction: column;
  margin-bottom: 4px;
`;

const Layout = styled.div<{ $isDesktop?: boolean }>`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 10px;
  ${(props) => !props.$isDesktop && `
    flex-direction: column;
    gap: 4px;
  `}
`;

const LabelWrapper = styled.div`
  display: flex;
  flex-direction: column;
  flex: 0 0 45%;
`;

const LabelText = styled.span<{ $mandatory?: boolean }>`
  font-weight: 600;
  color: var(--cadetGrey);
  font-size: 12px;


  ${(props) => props.$mandatory && `
    &::after {
      content: " *";
      color: var(--red, #d32f2f);
    }
  `}
`;

const TypeBadge = styled.small`
  font-size: 12px;
  color: var(--cadetGrey);
`;

const ValueText = styled.div<{ $isDesktop?: boolean }>`
  flex: 1;
  min-width: 0;
  font-size: 14px;
  color: var(--black, #222);
  text-align: ${(props) => (props.$isDesktop ? "right" : "left")};
  word-break: break-word;
`;

const EmptyValue = styled.span`
  color: #ccc;
  font-style: italic;
`;

interface Props {
  amount: number;
  type: any;
  date:string;
  title: string;
  createdAt:string;
  number: string;
  comment?: string;
}
const AttributesDoc = React.memo(function AttributesDoc({ title, value, data, mandatory, type }: AttributeItem) {

  const renderValue = () => {
    if (value === null || value === undefined || value === '') {
      return <EmptyValue>—</EmptyValue>;
    }

    const alias = type?.alias;

    if (alias === 'money' || alias === 'currency') {
      return <span>{formatPrice(Number(value))}</span>;
    }

    if (alias === 'date') {
      const d = new Date(String(value));
      const hasTime = d.getHours() !== 0 || d.getMinutes() !== 0;
      return <span>{hasTime ? formatDateTimeUA(String(value)) : formatDateUA(String(value))}</span>;
    }

    if (!Array.isArray(value)) {
      return <span>{data ? data[value as string] : String(value)}</span>;
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
        {(value as string[]).map((item, index) => (
          <span key={index}>{data ? data[item] : item}</span>
        ))}
      </div>
    );
  };

  return (
    <Row>
      <MediaQuery maxWidth={1239}>
        <Layout $isDesktop={false}>
          <LabelText $mandatory={mandatory}>{title}</LabelText>
          <ValueText $isDesktop={false}>{renderValue()}</ValueText>
        </Layout>
      </MediaQuery>

      <MediaQuery minWidth={1240}>
        <Layout $isDesktop={true}>
          <LabelWrapper>
            <LabelText $mandatory={mandatory}>{title}</LabelText>
          </LabelWrapper>
          <ValueText $isDesktop={true}>
            {renderValue()}
          </ValueText>
        </Layout>
      </MediaQuery>
    </Row>
  );
}, (prev, next) => prev.value === next.value && prev.title === next.title);

export default AttributesDoc;

export function AttributesMainDoc({ amount, type, date, title, createdAt, number, comment }: Props) {
  
  const fields = [
    { label: "Дата завантаження", value: formatDateTimeUA(createdAt) },
    { label: "Назва документу", value: title, truncate: true  },
    { label: "Тип документу", value: type?.title },
    { label: "Номер документу", value: number },
    { label: "Дата", value: formatDateUA(date) },
    { label: "Сума, грн", value: formatPrice(amount) },
    { label: "Коментар", value: comment, truncate: true },
  ];

  const renderValueWithTruncate = (field: { value: any; truncate?: boolean }) => {
    const { value, truncate } = field;
    if (value === null || value === undefined || value === '') return <EmptyValue>—</EmptyValue>;
    if (truncate && typeof value === 'string') {
      return (
        <AlertTooltip title={value}>
          <span style={{
            display: '-webkit-box',
            WebkitLineClamp: 1,
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            cursor: 'default',
            wordBreak: 'break-all',
          }}>
            {value}
          </span>
        </AlertTooltip>
      );
    }
    return value;
  };

  return (
    <Row>
      <MediaQuery maxWidth={1239}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {fields.map((field, idx) => (
            <Layout key={idx} $isDesktop={false}>
              <LabelText>{field.label}</LabelText>
              <ValueText $isDesktop={false}>{renderValueWithTruncate(field)}</ValueText>
            </Layout>
          ))}
        </div>
      </MediaQuery>

      <MediaQuery minWidth={1240}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px', flexWrap: 'wrap' }}>
          {fields.map((field, idx) => (
            <Layout key={idx} $isDesktop={true}>
              <LabelWrapper>
                <TypeBadge>{field.label}</TypeBadge>
              </LabelWrapper>
              <ValueText $isDesktop={true}>
                {renderValueWithTruncate(field)}
              </ValueText>
            </Layout>
          ))}
        </div>
      </MediaQuery>
    </Row>
  );
}